import React from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp } from "lucide-react";
import { Unit } from "../../../../stores/appOrderHistoryStore";
import { formatQuantityInfo } from "../../../../utils/quantityUtils";
import { ProductUsage, USAGE_WINDOW_DAYS } from "./productUsage";

interface UsageBadgeProps {
    usage: ProductUsage | null;
    // Compact renders icon + amount only (inside dense candidate rows).
    compact?: boolean;
}

/**
 * "340 kg /60d" — how much of this product the kitchen actually bought in the last
 * 60 days, the review flow's signal for whether a matched product is a real one.
 * Falls back to the order count when the lines carry no usable pack size (or
 * disagree about the unit), and zero orders in the window is highlighted since that
 * is the usual tell for a wrong match.
 */
const UsageBadge: React.FC<UsageBadgeProps> = ({ usage, compact }) => {
    const { t } = useTranslation();

    const orderCount = usage?.orderCount ?? 0;
    const colorClass = orderCount === 0
        ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
        : orderCount >= 3
            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';

    // Formatted through the shared quantity formatter, so g/ml roll up to kg/L and
    // wine-ish categories say "btl" exactly like the order history does.
    const quantityLabel = usage && usage.stdQuantity && usage.stdUnit
        ? formatQuantityInfo(
            usage.stdQuantity,
            { weightString: '', stdAmount: 1, stdMultiplier: 1, stdUnit: usage.stdUnit as Unit },
            usage.category,
        ).displayText
        : null;

    const title = orderCount === 0
        ? t('costPrice.automationReview.usageNoneTooltip', { days: USAGE_WINDOW_DAYS })
        : quantityLabel
            ? t('costPrice.automationReview.usageQuantityTooltip', {
                count: orderCount,
                days: USAGE_WINDOW_DAYS,
                quantity: quantityLabel,
            })
            : t('costPrice.automationReview.usageTooltip', {
                count: orderCount,
                days: USAGE_WINDOW_DAYS,
                quantity: Math.round((usage?.totalQuantity ?? 0) * 100) / 100,
            });

    return (
        <span
            title={title}
            className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0 ${colorClass}`}
        >
            <TrendingUp className="h-3 w-3" />
            {quantityLabel
                ? t('costPrice.automationReview.usageQuantity', { value: quantityLabel, days: USAGE_WINDOW_DAYS })
                : compact
                    ? t('costPrice.automationReview.usageCompact', { count: orderCount })
                    : orderCount === 0
                        ? t('costPrice.automationReview.usageNone', { days: USAGE_WINDOW_DAYS })
                        : t('costPrice.automationReview.usage', { count: orderCount, days: USAGE_WINDOW_DAYS })}
        </span>
    );
};

export default UsageBadge;
